import { writable, get } from 'svelte/store';
import { apiClient } from '@/utils/api';

// Types
export interface Message {
  id: string;
  conversation_id: string;
  sender: {
    id: string;
    name: string;
    avatar_url?: string;
  };
  content: string;
  metadata: Record<string, any>;
  read_at?: string;
  created_at: string;
  is_mine?: boolean;
  status?: 'sending' | 'sent' | 'failed';
}

export type ConversationStatus = 'pending' | 'accepted' | 'declined';
export type Inbox = 'accepted' | 'requests' | 'hidden';

export interface Conversation {
  id: string;
  status?: ConversationStatus;
  is_initiator?: boolean;
  quota_remaining?: number | null;
  hidden?: boolean;
  other_participant: {
    id: string;
    name: string;
    email?: string;
    avatar_url?: string;
  };
  last_message_preview?: string;
  last_message_at?: string;
  unread_count: number;
}

interface MessagesState {
  conversations: Conversation[];
  pendingRequestsCount: number;
  currentInbox: Inbox;
  currentConversation: Conversation | null;
  messages: Message[];
  isConversationsLoading: boolean;
  isConversationLoading: boolean;
  isSending: boolean;
  typingUsers: Set<string>;
  hasMoreMessages: boolean;
  messageCursor?: string;
  lastErrorCode?: string;
}

function csrfHeader(): Record<string, string> {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return csrf ? { 'X-CSRF-Token': csrf } : {};
}

// Store
function createMessagesStore() {
  const { subscribe, set, update } = writable<MessagesState>({
    conversations: [],
    pendingRequestsCount: 0,
    currentInbox: 'accepted',
    currentConversation: null,
    messages: [],
    isConversationsLoading: false,
    isConversationLoading: false,
    isSending: false,
    typingUsers: new Set(),
    hasMoreMessages: true,
    messageCursor: undefined,
  });

  function unwrap<T = any>(json: any): T {
    // Rails BaseController wraps successful responses in { data: ... }.
    return (json?.data ?? json) as T;
  }

  return {
    subscribe,

    async fetchConversations(inbox: Inbox = 'accepted') {
      update(s => ({ ...s, isConversationsLoading: true, currentInbox: inbox }));

      try {
        const response = await fetch(`/api/v1/conversations?inbox=${inbox}`, {
          headers: { Accept: 'application/json' },
        });
        const json = await response.json();
        const data = unwrap<{ conversations: Conversation[] }>(json);

        update(s => ({
          ...s,
          conversations: data.conversations ?? [],
          isConversationsLoading: false,
        }));
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
        update(s => ({ ...s, isConversationsLoading: false }));
      }
    },

    async fetchPendingRequestsCount() {
      try {
        const response = await fetch('/api/v1/conversations?inbox=requests&limit=1', {
          headers: { Accept: 'application/json' },
        });
        const json = await response.json();
        const data = unwrap<{ conversations: Conversation[]; has_more: boolean }>(json);
        // Use length as a lower bound; backend pagination prevents fetching all in one request.
        update(s => ({ ...s, pendingRequestsCount: data.conversations?.length ?? 0 }));
      } catch (error) {
        console.error('Failed to fetch pending requests count:', error);
      }
    },

    async openConversation(conversationId: string) {
      update(s => ({
        ...s,
        isConversationLoading: true,
        currentConversation: null,
        messages: [],
        messageCursor: undefined,
        typingUsers: new Set(),
      }));

      try {
        const [convResponse, messagesResponse] = await Promise.all([
          fetch(`/api/v1/conversations/${conversationId}`, { headers: { Accept: 'application/json' } }),
          fetch(`/api/v1/conversations/${conversationId}/messages`, { headers: { Accept: 'application/json' } }),
        ]);

        const convJson = await convResponse.json();
        const messagesJson = await messagesResponse.json();
        const convData = unwrap<{ conversation: Conversation }>(convJson);
        const messagesData = unwrap<{ messages: Message[]; has_more: boolean; next_cursor?: string }>(messagesJson);

        update(s => ({
          ...s,
          currentConversation: convData.conversation,
          messages: messagesData.messages ?? [],
          hasMoreMessages: messagesData.has_more,
          messageCursor: messagesData.next_cursor,
          isConversationLoading: false,
        }));
      } catch (error) {
        console.error('Failed to open conversation:', error);
        update(s => ({ ...s, isConversationLoading: false }));
      }
    },

    async loadMoreMessages() {
      const state = get({ subscribe });
      if (!state.currentConversation || !state.hasMoreMessages || state.isConversationLoading) return;

      update(s => ({ ...s, isConversationLoading: true }));

      try {
        const params = new URLSearchParams();
        if (state.messageCursor) params.set('cursor', state.messageCursor);

        const response = await fetch(
          `/api/v1/conversations/${state.currentConversation.id}/messages?${params}`,
          { headers: { Accept: 'application/json' } },
        );
        const json = await response.json();
        const data = unwrap<{ messages: Message[]; has_more: boolean; next_cursor?: string }>(json);

        update(s => ({
          ...s,
          messages: [...(data.messages ?? []), ...s.messages],
          hasMoreMessages: data.has_more,
          messageCursor: data.next_cursor,
          isConversationLoading: false,
        }));
      } catch (error) {
        console.error('Failed to load more messages:', error);
        update(s => ({ ...s, isConversationLoading: false }));
      }
    },

    async startConversation(userId: string | number) {
      try {
        const response = await fetch('/api/v1/conversations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...csrfHeader() },
          body: JSON.stringify({ user_id: userId }),
        });
        const json = await response.json();

        if (!response.ok) return null;

        const data = unwrap<{ conversation: Conversation }>(json);
        const conversation = data.conversation;
        if (!conversation) return null;

        update(s => {
          const conversations = s.conversations.filter(existing => existing.id !== conversation.id);
          return {
            ...s,
            conversations: [conversation, ...conversations],
          };
        });

        return conversation;
      } catch (error) {
        console.error('Failed to start conversation:', error);
        return null;
      }
    },

    async sendMessage(content: string): Promise<{ ok: boolean; code?: string; message?: string; quota_remaining?: number }> {
      const state = get({ subscribe });
      if (!state.currentConversation) return { ok: false, code: 'NO_CONVERSATION' };

      update(s => ({ ...s, isSending: true, lastErrorCode: undefined }));

      const tempId = `temp-${Date.now()}`;
      const tempMessage: Message = {
        id: tempId,
        conversation_id: state.currentConversation.id,
        sender: { id: 'current', name: 'Me' },
        content,
        metadata: {},
        created_at: new Date().toISOString(),
        is_mine: true,
        status: 'sending',
      };
      update(s => ({ ...s, messages: [...s.messages, tempMessage] }));

      try {
        const response = await fetch(
          `/api/v1/conversations/${state.currentConversation.id}/messages`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...csrfHeader() },
            body: JSON.stringify({ content }),
          },
        );
        const json = await response.json();

        if (!response.ok) {
          update(s => ({
            ...s,
            messages: s.messages.filter(m => m.id !== tempId),
            isSending: false,
            lastErrorCode: json.code,
          }));
          return { ok: false, code: json.code, message: json.message, quota_remaining: json.quota_remaining };
        }

        const data = unwrap<{ message: Message; quota_remaining: number | null; conversation_status: ConversationStatus }>(json);

        update(s => ({
          ...s,
          messages: s.messages.map(m =>
            m.id === tempId ? { ...data.message, is_mine: true, status: 'sent' as const } : m,
          ),
          currentConversation: s.currentConversation
            ? { ...s.currentConversation, status: data.conversation_status, quota_remaining: data.quota_remaining }
            : s.currentConversation,
          isSending: false,
        }));
        return { ok: true, quota_remaining: data.quota_remaining ?? undefined };
      } catch (error) {
        console.error('Failed to send message:', error);
        update(s => ({
          ...s,
          messages: s.messages.map(m =>
            m.id === tempId ? { ...m, status: 'failed' as const } : m,
          ),
          isSending: false,
        }));
        return { ok: false, code: 'NETWORK_ERROR' };
      }
    },

    async acceptConversation(conversationId: string) {
      const response = await apiClient.post(`/api/v1/conversations/${conversationId}/accept`, {});
      if (response.success) {
        update(s => ({
          ...s,
          currentConversation: s.currentConversation?.id === conversationId
            ? { ...s.currentConversation, status: 'accepted' }
            : s.currentConversation,
          conversations: s.conversations.map(c =>
            c.id === conversationId ? { ...c, status: 'accepted' } : c,
          ),
        }));
      }
      return response.success;
    },

    async declineConversation(conversationId: string) {
      const response = await apiClient.post(`/api/v1/conversations/${conversationId}/decline`, {});
      if (response.success) {
        update(s => ({
          ...s,
          currentConversation: s.currentConversation?.id === conversationId ? null : s.currentConversation,
          conversations: s.conversations.filter(c => c.id !== conversationId),
        }));
      }
      return response.success;
    },

    async hideConversation(conversationId: string) {
      const response = await apiClient.post(`/api/v1/conversations/${conversationId}/hide`, {});
      if (response.success) {
        update(s => ({
          ...s,
          currentConversation: s.currentConversation?.id === conversationId ? null : s.currentConversation,
          conversations: s.conversations.filter(c => c.id !== conversationId),
        }));
      }
      return response.success;
    },

    async unhideConversation(conversationId: string) {
      const response = await apiClient.delete(`/api/v1/conversations/${conversationId}/hide`);
      return response.success;
    },

    async report(targetType: 'Message' | 'User' | 'Post' | 'Comment', targetId: string, reason: string, description: string) {
      return apiClient.post('/api/v1/reports', {
        target_type: targetType,
        target_id: targetId,
        reason,
        description,
      });
    },

    addMessage(message: Message) {
      update(s => ({
        ...s,
        messages: [...s.messages, message],
      }));
    },

    setTyping(userId: string, isTyping: boolean) {
      update(s => {
        const typingUsers = new Set(s.typingUsers);
        if (isTyping) {
          typingUsers.add(userId);
        } else {
          typingUsers.delete(userId);
        }
        return { ...s, typingUsers };
      });
    },

    closeConversation() {
      update(s => ({
        ...s,
        currentConversation: null,
        messages: [],
        messageCursor: undefined,
        hasMoreMessages: true,
        typingUsers: new Set(),
      }));
    },

    reset() {
      set({
        conversations: [],
        pendingRequestsCount: 0,
        currentInbox: 'accepted',
        currentConversation: null,
        messages: [],
        isConversationsLoading: false,
        isConversationLoading: false,
        isSending: false,
        typingUsers: new Set(),
        hasMoreMessages: true,
        messageCursor: undefined,
      });
    },
  };
}

export const messages = createMessagesStore();
